'use client'

import { setGADataLayer } from 'analytics'
import { ALL_ANIMATED_MIME_TYPES } from 'creation/src/creation-tools/constants'
import CreationToolError from 'creation/src/creation-tools/types/error'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import { getUrlMimeType } from 'creation/src/creation-tools/utils/get-source-info'
import { validateSourceByInput } from 'creation/src/creation-tools/utils/validate-source'
import { useContext, type KeyboardEvent, type SyntheticEvent } from 'react'
import { useCreationStore } from '../store/provider'
import { GiphyIcon, VimeoIcon, YoutubeIcon } from './platform-icons'
import { WelcomeContext } from './welcome-context'

/**
 * Legacy structural reference: components/welcome/url/index.tsx.
 *
 * `getMappedUrl` is ported unchanged — it is the specification, not a draft
 * (docs/plans/2026-08-07-001-creation-welcome-measurements.md §6 and the task
 * brief both call this out explicitly: youtube/vimeo pass through unchanged
 * via `getUrlMimeType`, giphy.com urls are rewritten to media.giphy.com — or
 * returned as-is when no id can be parsed — and everything else is dropped).
 * Do not "fix" its GIPHY id parsing or its youtube/vimeo passthrough.
 *
 * Legacy also drives an error modal here via `CreationToolsWelcomeContext`
 * (`setError`/`setErrorSource`) — wired below via `WelcomeContext` (Task 17).
 * `setErrorSource` gets the raw input `value`, not the mapped `url` —
 * matching legacy's catch (`setErrorSource(value)` in
 * components/welcome/url/index.tsx) exactly. That means a re-validation via
 * `onChangeOutputType` (welcome-shell.tsx) re-runs `getMappedUrl` on the
 * original pasted text, not the already-resolved media url; this is legacy's
 * own behavior, not a bug introduced here.
 *
 * Legacy's `isValidating`/`DotsLoader` opacity-fade overlay is intentionally
 * NOT ported: it dims this component's own block, but legacy also flips
 * `CreationToolsWelcomeContext`'s `isLoading` in the same synchronous update
 * (`setIsValidating(true); setIsLoading(true)`), which makes
 * `welcome/index.tsx` swap its *entire* `Container` — this input included —
 * for a full-screen `Loader` first (ported in welcome-shell.tsx, Task 3 of
 * this fix pass). The local dim/DotsLoader never has a chance to render
 * under real legacy behavior; porting it here without also porting that
 * would be inventing a visible loading state legacy itself doesn't show.
 * `setIsLoading(true)` below IS ported, at legacy's own position (right
 * after a mapped url resolves, before the login gate). There is no
 * `setIsLoading(false)` anywhere in legacy's `onInput`, on either path: a
 * successful validation always navigates away via the store's `setFiles`
 * (unmounting this component), and the error path only resets
 * `isValidating` — it re-sets `isLoading` back to `true` again, not `false`
 * (components/welcome/url/index.tsx:108-109, legacy's own quirk). Harmless
 * either way because `error` is checked before `isLoading` in
 * welcome-shell.tsx's ternary, so the error modal wins regardless; `onGoBack`
 * resets `isLoading` to `false` once the user leaves the error state.
 *
 * Legacy's `!user.loggedIn` early return (before any analytics, before
 * validating) remains here, unlike file-button.tsx — that file has no
 * loggedIn check of its own to begin with, so it relies entirely on
 * `setFiles`'s internal gate. This component fires analytics and calls
 * `validateSourceByInput` (a real network request for youtube/vimeo urls)
 * *before* `setFiles` is ever reached, so `setFiles`'s gate is too late to
 * prevent either. Unlike legacy's redirect, the modal can preserve this
 * component and resume the captured URL after login; the continuation passed
 * to `requireLogin` below does exactly that.
 */
const getMappedUrl = (source: string): string => {
    const mimeType = getUrlMimeType(source)

    // valid source
    if (mimeType) {
        return source
    }

    // giphy urls
    if (source.indexOf('giphy.com') > -1) {
        const isClip = source.indexOf('/clips/') > -1
        const filename = isClip ? 'giphy360p.mp4' : 'giphy.gif'
        const path = source.split('?')[0].split('/').pop()
        const gifId = path ? path.split('-').pop() : ''
        return gifId ? `https://media.giphy.com/media/${gifId}/${filename}` : source
    }

    return ''
}

const WelcomeUrlInput = () => {
    const setFiles = useCreationStore((state) => state.setFiles)
    const isLoggedIn = useCreationStore((state) => state.isLoggedIn)
    const requireLogin = useCreationStore((state) => state.requireLogin)
    const { setError, setErrorSource, setIsLoading } = useContext(WelcomeContext)

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return

        const value = e.currentTarget.value
        if (!value || getMappedUrl(value)) return

        e.preventDefault()
        setError(CreationToolError.InvalidMediaUrl)
        setErrorSource(value)

        setGADataLayer({
            event: 'choose_file_error',
            options: {
                form: {
                    formName: 'choose file',
                    formError: CreationToolError.gaLabel(CreationToolError.InvalidMediaUrl, value),
                },
                content: {
                    contentFormat: 'gif',
                    contentType: 'url',
                },
            },
        })
    }

    const validateUrl = async (value: string, url: string) => {
        setIsLoading(true)

        const content = {
            contentFormat: 'gif',
            contentType: 'url',
        }

        // Legacy bug, ported verbatim (task brief): `choose_file_click` and
        // `choose_file_success` double-nest `options.options.form` while
        // `choose_file_error` below uses the flat `options.form` shape. Do
        // not "fix" this here — see file-button.tsx for the same pattern on
        // the file-picker path.
        setGADataLayer({
            event: 'choose_file_click',
            options: {
                options: {
                    form: {
                        formName: 'choose file',
                    },
                },
                content,
            },
        })

        try {
            const validatedFile = await validateSourceByInput(url, ALL_ANIMATED_MIME_TYPES)
            setFiles([validatedFile], CreationToolOutputType.Gif)

            setGADataLayer({
                event: 'choose_file_success',
                options: {
                    options: {
                        form: {
                            formName: 'choose file',
                        },
                    },
                    content,
                },
            })
        } catch (err) {
            setError(err as CreationToolError)
            setErrorSource(value)

            setGADataLayer({
                event: 'choose_file_error',
                options: {
                    form: {
                        formName: 'choose file',
                        formError: CreationToolError.gaLabel(err as CreationToolError, url),
                    },
                    content,
                },
            })
        }
    }

    const onInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { value = '' } = e.currentTarget
        const url = getMappedUrl(value)

        if (!url) return

        // Gate before raising the full-screen loader. The modal keeps this
        // route and input mounted; its success callback flips the store's
        // session state first, then resumes this exact captured source.
        if (!isLoggedIn) {
            requireLogin(() => validateUrl(value, url))
            return
        }

        await validateUrl(value, url)
    }

    return (
        <div className="relative mr-[15px] w-full rounded-[4px] bg-[rgba(236,236,236,0.1)] px-10 pt-5 pb-[30px] text-[15px] text-left last:mr-0">
            <h3 className="mb-[10px] text-left text-[17px] leading-5 font-black">Any URL</h3>
            <p className="mb-3 max-w-full text-[15px] leading-[1.3]">
                We support media URLs from GIPHY, YouTube, Vimeo, &amp; many others!
            </p>
            <div className="flex h-[50px] items-center rounded-[2px] bg-white px-3">
                <input
                    type="url"
                    placeholder="Paste any media or GIF URL"
                    onInput={onInput}
                    onKeyDown={onKeyDown}
                    className="h-[50px] w-full rounded-[inherit] border-0 bg-transparent p-0 text-black outline-none placeholder:text-[color:var(--input-placeholder)]"
                />
                <GiphyIcon />
                <YoutubeIcon />
                <VimeoIcon />
            </div>
        </div>
    )
}

export default WelcomeUrlInput
